import { useCallback, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  FilterState,
  createDefaultFilterState,
} from '@/utilities/types/shared-filter'
import type {
  FilterConfig,
  UpdateDateRangeParams,
} from '@/utilities/types/shared-filter'
import { UserFilterField } from '../filter-user/UserFilterField'

type AlertSeverity = 'success' | 'error' | 'warning' | 'info'

export interface FilterAreasModalProps<T = any> {
  open: boolean
  onClose: () => void
  config: FilterConfig<T>
  onApplyFilter: (filters: FilterState<T>) => void
  initialFilters: FilterState<T>
  tab?: string
  optimizedDateFilterAreas: (
    sinceCreated: string,
    untilCreated: string,
    sinceModified: string,
    untilModified: string,
  ) => void
  handleAlertMessage: (message: string, severity: AlertSeverity) => void
  handleReset: () => void
}

export function FilterAreasModal<T>({
  open,
  onClose,
  config,
  onApplyFilter,
  initialFilters,
  tab,
  optimizedDateFilterAreas,
  handleAlertMessage,
  handleReset,
}: FilterAreasModalProps<T>) {
  const [currentFilters, setCurrentFilters] = useState(() => ({
    ...createDefaultFilterState<T>(),
    ...initialFilters,
  }))

  const handleUpdateFilter = useCallback((field: string, value: string[]) => {
    setCurrentFilters((prev) => ({
      ...prev,
      regularFilters: {
        ...prev.regularFilters,
        [field]: value,
      },
    }))
  }, [])

  const handleDateRangeChange = useCallback(
    ({ field, range }: UpdateDateRangeParams) => {
      setCurrentFilters((prev) => ({
        ...prev,
        dateRanges: {
          ...prev.dateRanges,
          [field]: range,
        },
      }))
    },
    [],
  )

  const handleApply = useCallback(() => {
    const { createdDate, modifiedDate } = currentFilters.dateRanges

    const sinceCreated = createdDate?.start || ''
    const untilCreated = createdDate?.end || ''
    const sinceModified = modifiedDate?.start || ''
    const untilModified = modifiedDate?.end || ''

    if (
      createdDate?.start &&
      createdDate?.end &&
      createdDate.start > createdDate.end
    ) {
      handleAlertMessage(
        'Created date start cannot be greater than end date.',
        'error',
      )
      return
    }

    if (
      modifiedDate?.start &&
      modifiedDate?.end &&
      modifiedDate.start > modifiedDate.end
    ) {
      handleAlertMessage(
        'Modified date start cannot be greater than end date.',
        'error',
      )
      return
    }

    if (
      createdDate?.start &&
      modifiedDate?.start &&
      modifiedDate.start < createdDate.start
    ) {
      handleAlertMessage(
        'Modified date cannot be earlier than created date.',
        'error',
      )
      return
    }

    optimizedDateFilterAreas(
      sinceCreated,
      untilCreated,
      sinceModified,
      untilModified,
    )
    onApplyFilter(currentFilters)
    onClose()
  }, [
    currentFilters,
    optimizedDateFilterAreas,
    onApplyFilter,
    onClose,
    handleAlertMessage,
  ])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: '400px', p: 2 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Typography variant="h6" fontFamily="Gabarito">
          Filter By:
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ mt: 2 }}>
            {config.fields.map((fieldConfig) => (
              <UserFilterField
                key={fieldConfig.field}
                config={fieldConfig}
                filters={currentFilters}
                onFilterChange={handleUpdateFilter}
                onDateRangeChange={handleDateRangeChange}
                tab={tab}
              />
            ))}
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ p: 0, mt: 2 }}>
        <Button
          onClick={handleReset}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontFamily: 'Gabarito',
          }}
        >
          Reset Filter
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            bgcolor: 'secondary.main',
            textTransform: 'none',
            fontFamily: 'Gabarito',
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  )
}
